import React from 'react';
import { useNavigate } from 'react-router-dom';
import { formatRelativeTime } from '../../hooks/use-relative-time';
import styles from './TopicFeedCard.module.css';

type TopicFeedCardProps = {
  readonly topicId: string;
  readonly title: string;
  // Known values are 'belief' and 'discussion' (see TYPE_CONFIG); any other string falls back
  // to the discussion config. Typed as string since it is only ever used as a TYPE_CONFIG key.
  readonly type: string;
  readonly creatorId: string;
  readonly creatorName: string;
  readonly postCount?: number;
  readonly lastActivityAt: string;
  readonly createdAt: string;
};

const TYPE_CONFIG: Record<string, { readonly icon: string; readonly label: string; readonly className: string }> = {
  belief: { icon: '◈', label: 'Belief', className: 'belief' },
  discussion: { icon: '◎', label: 'Discussion', className: 'discussion' },
};

const TopicFeedCard = ({ topicId, title, type, creatorId, creatorName, postCount, lastActivityAt, createdAt }: TopicFeedCardProps): React.ReactElement => {
  const navigate = useNavigate();
  const config = TYPE_CONFIG[type] ?? TYPE_CONFIG['discussion'];
  const count = postCount ?? 0;
  const isNew = count === 0;
  const isActive = count >= 5;

  return (
    <div
      className={`${styles.card} ${styles[config.className]}`}
      onClick={(): void => { void navigate(`/forum/${topicId}`); }}
      role="link"
      tabIndex={0}
      data-testid="topic-card"
    >
      <span className={`${styles.badge} ${styles[`badge_${config.className}`]}`}>
        {config.icon} {config.label}
      </span>

      <div className={styles.title}>{title}</div>

      <div className={styles.byline}>
        <span
          className={styles.creator}
          onClick={(e): void => { e.stopPropagation(); void navigate(`/entity/${creatorId}`); }}
          role="link"
          tabIndex={0}
          data-testid="topic-card-creator"
        >
          {creatorName}
        </span>
        <span className={styles.bylineVerb}>
          {isNew ? ' started a thread' : ` · ${count} post${count === 1 ? '' : 's'}`}
        </span>
      </div>

      <div className={styles.meta}>
        {isActive && <span className={styles.activeTag}>active</span>}
        <span>{formatRelativeTime(lastActivityAt)}</span>
        {lastActivityAt !== createdAt && (
          <>
            <span className={styles.sep}>·</span>
            <span>started {formatRelativeTime(createdAt)}</span>
          </>
        )}
      </div>

      <div className={styles.promptRow}>
        <button type="button" className={styles.promptBtn} onClick={(e): void => { e.stopPropagation(); void navigate(`/forum/${topicId}`); }} data-testid="topic-card-prompt">
          {isNew ? 'be first to respond' : 'join discussion'}
        </button>
      </div>
    </div>
  );
};

export { TopicFeedCard };
export type { TopicFeedCardProps };
